import { FlatUploadRequirementsData, NestedUploadRequirementsData } from '../../interfaces/UploadRequirementsInterface';
import { PossibleUploadStates } from '../../interfaces/UploadRequirementsInterface';
import { SchoolConfigEntry } from '../../interfaces/EOAInterface';

export const districtRequiresTestScores = (schoolsConfig: SchoolConfigEntry[]): boolean => {
  const primarySchools = schoolsConfig.filter((school) => school.is_primary_on_eoa !== false);
  if (primarySchools.length === 0) return false;

  return primarySchools.some((school) => school.required_act_for_graduation || school.required_sat_for_graduation);
};

export type UploadProgressState = 'pending' | 'progress' | 'completed';
type ResultObjectArguments = {
  uploadedSchoolsCount: number;
  totalSchoolsCount: number;
  hasStarted: boolean;
};
const createResultObject = ({ uploadedSchoolsCount, totalSchoolsCount, hasStarted }: ResultObjectArguments) => {
  /*
    pending - no data uploaded at all
    progress:
      - description is "x/y schools data uploaded"
        * full data for at least 1 school has been uploaded when its nested structure
        * at least 1 file was successfully processed when its flat structure
      - description is "in progress"
        * some data for a school has been uploaded but no school has full data when its nested structure
        * file have been uploaded but not successfully processed when its flat structure
    completed: all required data is uploaded
  */

  let state: UploadProgressState = 'pending';
  let description = `${uploadedSchoolsCount}/${totalSchoolsCount} Schools Data Uploaded`;

  if (uploadedSchoolsCount === 0) {
    description = hasStarted ? 'In Progress' : 'Not Started';
  }
  if ((uploadedSchoolsCount > 0 && uploadedSchoolsCount !== totalSchoolsCount) || hasStarted) state = 'progress';
  if (uploadedSchoolsCount === totalSchoolsCount) state = 'completed';

  return { state, description, uploadPercent: (uploadedSchoolsCount / totalSchoolsCount) * 100 };
};

export const calculateFlatDataUploadProgress = (flatData: FlatUploadRequirementsData) => {
  let uploadedSchoolsCount = 0;
  let schoolNames = Object.keys(flatData.schools);
  let hasStarted = false;

  schoolNames.forEach((schoolName) => {
    let isNotUploaded = flatData.schools[schoolName] === PossibleUploadStates.NOT_YET_UPLOADED;
    let isNotApplicable = flatData.schools[schoolName] === PossibleUploadStates.NOT_APPLICABLE;

    if (!isNotUploaded && !isNotApplicable) hasStarted = true;
    if (flatData.schools[schoolName] === PossibleUploadStates.UPLOADED) uploadedSchoolsCount++;
  });

  return createResultObject({ uploadedSchoolsCount, totalSchoolsCount: schoolNames.length, hasStarted });
};

type SchoolProgress = 'pending' | 'progress' | 'completed';
export const calculateFlatDataUploadCategoryProgressBySchool = (flatData: FlatUploadRequirementsData) => {
  let schoolsProgress: { [key: string]: SchoolProgress } = {};

  let schoolNames = Object.keys(flatData.schools);

  schoolNames.forEach((schoolName) => {
    let state: SchoolProgress = 'pending';

    if (flatData.schools[schoolName] === PossibleUploadStates.UPLOAD_IN_PROGRESS) state = 'progress';
    if (flatData.schools[schoolName] === PossibleUploadStates.UPLOADED) state = 'completed';

    schoolsProgress[schoolName] = state;
  });

  return schoolsProgress;
};

export const calculateNestedDataUploadProgress = (nestedData: NestedUploadRequirementsData) => {
  let uploadedSchoolsCount = 0;
  let schoolNames = Object.keys(nestedData.schools);
  let hasStarted = false;

  schoolNames.forEach((schoolName) => {
    let uploadStatuses = Object.values(nestedData.schools[schoolName]);

    let hasProgress = uploadStatuses.some((uploadStatus) => {
      let isNotUploaded = uploadStatus === PossibleUploadStates.NOT_YET_UPLOADED;
      let isNotApplicable = uploadStatus === PossibleUploadStates.NOT_APPLICABLE;

      return !isNotUploaded && !isNotApplicable;
    });

    if (hasProgress) hasStarted = true;
    let isAllDataUploaded = uploadStatuses.every((uploadStatus) => uploadStatus === PossibleUploadStates.UPLOADED);
    if (isAllDataUploaded) uploadedSchoolsCount++;
  });

  return createResultObject({ uploadedSchoolsCount, totalSchoolsCount: schoolNames.length, hasStarted });
};

export const calculateNestedDataUploadCategoryProgressBySchool = (nestedData: NestedUploadRequirementsData) => {
  let schoolsProgress: { [key: string]: SchoolProgress } = {};

  let schoolsNames = Object.keys(nestedData.schools);

  schoolsNames.forEach((schoolName) => {
    let state: SchoolProgress = 'pending';
    let uploadStatuses = Object.values(nestedData.schools[schoolName]);

    let hasProgress = uploadStatuses.some((uploadStatus) => {
      let isNotUploaded = uploadStatus === PossibleUploadStates.NOT_YET_UPLOADED;
      let isNotApplicable = uploadStatus === PossibleUploadStates.NOT_APPLICABLE;

      return !isNotUploaded && !isNotApplicable;
    });

    if (hasProgress) state = 'progress';
    if (uploadStatuses.every((uploadStatus) => uploadStatus === PossibleUploadStates.UPLOADED)) state = 'completed';

    schoolsProgress[schoolName] = state;
  });

  return schoolsProgress;
};
